import * as React from "react";
import { flushSync } from "react-dom";
import { useAuth } from "../../../lib/auth/hooks";
import { setProApiTokenRefreshHandler } from "../../../lib/api/pro-api-auth";
import { setUserServiceTokenRefreshHandler } from "../../../lib/api/user-service-auth";
import { setSessionExpiredHandler } from "../../../lib/auth/session-expired";
import { useSessionExpiryTimer } from "../../../lib/auth/session-expiry-timer";
import { useSessionInvalidationSocket } from "../../../lib/auth/session-invalidation-socket";
import {
  bounceToAccountLogin,
  isAccountAppHost,
} from "../../../lib/auth/app-origins";

const AuthReadyContext = React.createContext(true);

export function useAuthReady(): boolean {
  return React.useContext(AuthReadyContext);
}

/**
 * Provides auth context and kicks off background session hydrate.
 * Does not block the tree on network — JWT is decoded locally into atoms on
 * first paint so the shell can render immediately.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { initializeAuth, refreshAccessToken, clearAuth } = useAuth();

  useSessionExpiryTimer();
  useSessionInvalidationSocket();

  React.useEffect(() => {
    setProApiTokenRefreshHandler(refreshAccessToken);
    setUserServiceTokenRefreshHandler(refreshAccessToken);
    setSessionExpiredHandler(() => {
      // Force the atom-clear's re-render to commit, then wait for an actual
      // paint before tearing the page down — without this, the synchronous
      // hard navigate below can outrun React and skip straight past the
      // "Redirecting to sign in…" screen AppShell renders once isAuthenticated
      // flips false, leaving a blank flash instead.
      flushSync(() => clearAuth());
      if (typeof window === "undefined") return;
      const navigate = () => {
        if (isAccountAppHost()) {
          if (!window.location.pathname.startsWith("/login")) {
            const redirect = encodeURIComponent(window.location.href);
            window.location.replace(`/login?redirect=${redirect}`);
          }
          return;
        }
        bounceToAccountLogin(window.location.href);
      };
      // Double rAF: the first runs once a paint is scheduled, the second
      // guarantees that paint has actually happened before we navigate away.
      requestAnimationFrame(() => requestAnimationFrame(navigate));
    });
    return () => {
      setProApiTokenRefreshHandler(null);
      setUserServiceTokenRefreshHandler(null);
      setSessionExpiredHandler(null);
    };
  }, [refreshAccessToken, clearAuth]);

  React.useEffect(() => {
    void initializeAuth();
  }, [initializeAuth]);

  return (
    <AuthReadyContext.Provider value={true}>
      {children}
    </AuthReadyContext.Provider>
  );
}
